import React, { useCallback } from 'react';
import { FlameIcon } from 'lucide-react';

import { useSocialMediaOpportunitiesStore } from '../../../model/social-media-opportunities-widget.store';
import { useOpportunityFilters } from '../../../model/hooks';
import type {
  OpportunityItemWithFavorite,
  OpportunitiesListProps,
} from './OpportunitiesList.types';
import { OpportunitiesFilter } from './OpportunitiesFilter';
import { getWidgetStores } from '@/entities/widget';
import { OpportunitiesGrid } from './components/OpportunitiesGrid';
import { FavoritesEmptyState, OpportunitiesEmptyState } from '../EmptyStates';
import { useTranslation } from 'react-i18next';

/**
 * Presentational component for displaying a list of opportunities
 * Can be used with or without the SocialMediaOpportunitiesProvider
 */
export const OpportunitiesList = ({
  opportunities,
  onCardClick,
  onOpenDetail,
  showFilters = true,
  isPresentational = false,
  isLoading = false,
  favoritesOnly = false,
  onFavoritesFilterToggle,
  onToggleFavorite,
}: OpportunitiesListProps): React.JSX.Element => {
  const { t } = useTranslation('analysis');

  const {
    filteredOpportunities,
    topOpportunities,
    recentOpportunities,
    filters,
    audienceOptions,
    hasActiveFilters,
    updateFilter,
    resetFilters,
  } = useOpportunityFilters(opportunities);

  const handleTargetAudiencesChange = useCallback(
    (audiences: string[]) => updateFilter('targetAudiences', audiences),
    [updateFilter],
  );

  // single call site, large closure — inlined as a plain computed value
  // rather than a `renderOpportunitySections`-style closure.
  const opportunitySections: React.ReactNode = favoritesOnly ? (
    <OpportunitiesGrid
      opportunities={filteredOpportunities}
      onCardClick={onCardClick}
      onOpenDetail={onOpenDetail}
      isPresentational={isPresentational}
      isLoading={isLoading}
      hasActiveFilters={hasActiveFilters}
      favoritesOnly={favoritesOnly}
      onToggleFavorite={onToggleFavorite}
    />
  ) : (
    <>
      {topOpportunities.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1 rounded-lg bg-red-100 px-3 py-2">
              <FlameIcon className="size-5 text-red-600" />
              <span className="text-body-md text-red-950">
                {t('socialMediaOpportunitiesWidget.opportunitiesList.topOpportunities')}
              </span>
            </div>
          </div>
          <OpportunitiesGrid
            opportunities={topOpportunities}
            onCardClick={onCardClick}
            onOpenDetail={onOpenDetail}
            isPresentational={isPresentational}
            isLoading={isLoading}
            hasActiveFilters={hasActiveFilters}
            onToggleFavorite={onToggleFavorite}
          />
        </div>
      )}
      {recentOpportunities.length > 0 && (
        <div>
          <div className="py-4">
            <span className="text-body-lg font-medium text-foreground">
              {t('socialMediaOpportunitiesWidget.opportunitiesList.otherOpportunities')}
            </span>
          </div>
          <OpportunitiesGrid
            opportunities={recentOpportunities}
            onCardClick={onCardClick}
            onOpenDetail={onOpenDetail}
            isPresentational={isPresentational}
            isLoading={isLoading}
            hasActiveFilters={hasActiveFilters}
            onToggleFavorite={onToggleFavorite}
          />
        </div>
      )}
    </>
  );

  if (isPresentational) {
    return (
      <div className="flex flex-col w-full h-full px-0">
        <OpportunitiesGrid
          opportunities={opportunities}
          onCardClick={onCardClick}
          onOpenDetail={onOpenDetail}
          isPresentational={isPresentational}
          isLoading={isLoading}
          hasActiveFilters={hasActiveFilters}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full px-10 py-2 gap-5">
      {showFilters && (
        <>
          <OpportunitiesFilter
            filterOptions={{
              targetAudiences: audienceOptions,
            }}
            selectedTargetAudiences={filters.targetAudiences}
            hasActiveFilters={hasActiveFilters}
            onTargetAudiencesChange={handleTargetAudiencesChange}
            onResetFilters={resetFilters}
            favoritesOnly={favoritesOnly}
            onFavoritesFilterToggle={onFavoritesFilterToggle}
          />
        </>
      )}
      <div className="w-full h-full">{opportunitySections}</div>
    </div>
  );
};

/**
 * Wrapper component that connects OpportunitiesList to SocialMediaOpportunitiesProvider
 * Use this when you need the full context integration (details view, item selection, etc.)
 */
export const OpportunitiesListWithContext = ({
  opportunities,
  isLoading = false,
  favoritesOnly = false,
  onFavoritesFilterToggle,
  onToggleFavorite,
}: {
  opportunities: OpportunityItemWithFavorite[];
  isLoading?: boolean;
  favoritesOnly?: boolean;
  onFavoritesFilterToggle?: (favoritesOnly: boolean) => void;
  onToggleFavorite?: (opportunityId: string, nextFavorite: boolean) => Promise<void>;
}): React.JSX.Element => {
  const setDetailsView = useSocialMediaOpportunitiesStore((state) => state.setDetailsView);
  const setSelectedItem = useSocialMediaOpportunitiesStore((state) => state.setSelectedItem);

  const handleCardClick = useCallback(
    (item: OpportunityItemWithFavorite) => {
      setDetailsView(true);
      setSelectedItem(item);
      // Store access via the widget binding seam.
      // See `entities/widget/lib/widget-store-bindings`.
      const { chatStore } = getWidgetStores();
      chatStore.useChatStore
        .getState()
        .setInputOptions({ selectedOpportunity: item.opportunityId });
    },
    [setDetailsView, setSelectedItem],
  );

  if (opportunities.length < 1 && !isLoading) {
    return (
      <div className="flex flex-col w-full h-full px-10 pt-2 pb-10 gap-5">
        <OpportunitiesFilter
          filterOptions={{
            targetAudiences: [],
          }}
          favoritesOnly={favoritesOnly}
          onFavoritesFilterToggle={onFavoritesFilterToggle}
          disabled
        />
        {favoritesOnly ? <FavoritesEmptyState /> : <OpportunitiesEmptyState />}
      </div>
    );
  }

  return (
    <OpportunitiesList
      opportunities={opportunities}
      onCardClick={handleCardClick}
      showFilters={true}
      isLoading={isLoading}
      favoritesOnly={favoritesOnly}
      onFavoritesFilterToggle={onFavoritesFilterToggle}
      onToggleFavorite={onToggleFavorite}
    />
  );
};
